import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Helpers
import { is_gen_2, isGen2LightDevice, isPlusRgbwPm, isRgbwwPm } from 'src/helpers/interface_helpers';
import { getComponentByDeviceMode } from '../core/local_action_components_helpers';
import { getTargetOptions } from '../core/target_local_options';
import isDimmerWithCalibration from 'src/helpers/is_calibratable_dimmer';
//Types
import type { ActionJob, LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
import type { Device } from 'src/types/Device';

export const getLightLocalActions = (
	action: ActionJob,
	mainDevice: Device,
	params: { id: number },
	scheduleAction = false,
): LocalControlOptions[] => {
	const $format = get(format);
	const actionParams = action.params;
	const component = getComponentByDeviceMode(mainDevice);
	const includeOffsetWhite = scheduleAction
		? isPlusRgbwPm(mainDevice.type) || isRgbwwPm(mainDevice.type)
		: !isGen2LightDevice(mainDevice) && !isDimmerWithCalibration(mainDevice?.type);

	let mode = component.mode;
	const isRgbMode = /^(color|rgbcct|rgbw?)$/.test(mode);
	const deviceGen = is_gen_2(mainDevice) ? 2 : 1;

	if (isRgbMode && deviceGen === 1) mode = 'rgbw';

	/**
	 * 1. Common attributes & params used in both branches
	 */
	const attr = { target: getTargetOptions(mainDevice) };
	const toggleAfterAttr = { ...attr, toggle_after: Boolean(actionParams?.toggle_after) };
	const hasCT = mode === 'rgbcct' || mode === 'cct' || component.colorTemp;

	/**
	 * 2. Define the primary "Set" action dynamically
	 */
	const controlAction: LocalControlOptions = {
		title: $format(isRgbMode ? `local_action_control_${mode}_light` : 'local_action_control_light', {
			default: isRgbMode ? `Control ${mode.toUpperCase()} Lights` : 'Control Light',
		}),
		value: `${mode}.set`,
		attr: {
			...toggleAfterAttr,
			brightness: actionParams?.brightness != undefined,
			transition_duration: actionParams?.transition_duration != undefined,
			offset: actionParams?.offset != undefined,
			...(includeOffsetWhite && { offset_white: actionParams?.offset_white != undefined }),
			...(isRgbMode && { rgb: actionParams?.rgb != undefined }),
			...(mode === 'rgbw' && { white: actionParams?.white != undefined }),
			...(hasCT && { ct: actionParams?.ct != undefined }),
		},
		params: {
			id: params.id,
			on: actionParams?.on || false,
			toggle_after: actionParams?.toggle_after || 0,
			brightness: actionParams?.brightness != undefined ? actionParams.brightness : 50,
			transition_duration:
				actionParams?.transition_duration != undefined ? actionParams.transition_duration : null,
			offset: actionParams?.offset != undefined ? actionParams.offset : 0,
			...(includeOffsetWhite && {
				offset_white: actionParams?.offset_white != undefined ? actionParams.offset_white : 0,
			}),
			...(isRgbMode && { rgb: actionParams?.rgb || [255, 0, 0] }),
			...(mode === 'rgbw' && {
				white: actionParams?.white != undefined ? actionParams.white : 127,
			}),
			...(hasCT && { ct: actionParams?.ct != undefined ? actionParams.ct : null }),
		},
		onOffState: {
			checked: actionParams?.on != undefined,
			selectBox: true,
			label: $format('label_light_state', { default: 'Light State' }),
		},
	};

	/**
	 * 3. Assemble the result array
	 */
	const localControlOptions: LocalControlOptions[] = [
		controlAction,
		{
			title: $format('local_action_toggle_light', { default: 'Toggle Light' }),
			value: `${isRgbMode && deviceGen === 1 ? 'light' : mode}.toggle`,
			attr,
			params,
		},
	];

	/**
	 * 4. Add config mode if applicable
	 */
	if (mode === 'rgbcct') {
		localControlOptions.push({
			title: $format('local_action_set_mode', { default: 'Set Mode property' }),
			value: `${mode}.setconfig`,
			attr: { ...attr, config: true },
			params: { ...params, config: { mode: actionParams?.config?.mode || 'rgb' } },
		});
	}

	return localControlOptions;
};
